import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { usePrivy } from "@privy-io/react-auth";
import { FloatingLeftButton } from "@/components/FloatingLeftButton";
import { useRushLeaderboard, useStats } from "@/lib/hooks/useApi";
import type { CustomLeaderboard } from "@/routes/(game)/leaderboard/-components/types";
import AnimatedLoading from "@/components/AnimatedLoading";
import { DefaultButton } from "@/components/DefaultButton";
import { UserLeaderboardList } from "@/components/UserLeaderboardList";
import { REWARD_ICONS, USDC_ICON } from "@/lib/utils";
import { RushLeaderboard } from "./-components/rush-leaderboard";
import { DatePickerCalendar } from "@/components/DatePickerCalendar";
import AnimatedWaiting from "@/components/AnimatedWaiting";
import dayjs from "dayjs";
import { t } from "@/lib/i18n";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { toast } from "sonner";
import { RUSH_GRAND_RUSH_REWARD_SWAP_AT } from "@/lib/constants";
import { useEpochReached } from "@/lib/hooks/useEpochReached";

dayjs.extend(utc);
dayjs.extend(timezone);

const getUtcEpochFromDate = (date: Date) => {
  const d = dayjs(date);
  return Math.floor(Date.UTC(d.year(), d.month(), d.date(), 0, 0, 0) / 1000);
};

const getDateFromEpoch = (epoch?: number) => {
  if (!epoch || !Number.isFinite(epoch)) return undefined;
  // Use UTC to ensure the date represents the UTC day, not local timezone
  const utcDate = dayjs.unix(epoch).utc();
  // Create a Date object using UTC components to avoid timezone conversion issues
  return new Date(Date.UTC(utcDate.year(), utcDate.month(), utcDate.date()));
};

type RushLeaderboardSearch = {
  date?: number;
};

export const Route = createFileRoute("/(game)/rush/leaderboard")({
  validateSearch: (search): RushLeaderboardSearch => {
    const numericDate = Number(
      typeof search.date === "string" || typeof search.date === "number"
        ? search.date
        : undefined,
    );

    const result: RushLeaderboardSearch = {};

    if (Number.isFinite(numericDate) && numericDate > 0) {
      result.date = Math.floor(numericDate);
    }

    return result;
  },
  component: RushLeaderboardRoute,
});

const UNVERIFIED_TOAST_ID = "toast:rush-leaderboard-unverified";

function RushLeaderboardRoute() {
  const navigate = useNavigate();
  const { user } = usePrivy();
  const walletAddress = user?.wallet?.address ?? "";
  const search = Route.useSearch();
  const hasInitializedRef = useRef(false);

  const { data: stats } = useStats(walletAddress);
  const gameWalletAddress = stats?.embeddedWallet;
  const hasRewardSwapped = useEpochReached(RUSH_GRAND_RUSH_REWARD_SWAP_AT);
  const rushLeaderboardPrizeList = hasRewardSwapped
    ? ["$50", "$30", "$20"]
    : ["$50", "$30", "$20"];

  useEffect(() => {
    if (!walletAddress || stats?.isVerified !== false) {
      toast.dismiss(UNVERIFIED_TOAST_ID);
      return;
    }

    toast.warning("YOUR ACCOUNT IS UNVERIFIED", {
      id: UNVERIFIED_TOAST_ID,
      description:
        "Link Twitter/X and at least 1 other social to get verified and receive weekly rewards.",
      duration: Infinity,
      closeButton: false,
      dismissible: false,
      className: "flex flex-col",
      action: (
        <div className="flex w-full justify-end space-x-1">
          <DefaultButton
            variant="white"
            className="uppercase text-black"
            onClick={() => toast.dismiss(UNVERIFIED_TOAST_ID)}
          >
            Close
          </DefaultButton>
          <DefaultButton
            className="uppercase"
            onClick={() => {
              toast.dismiss(UNVERIFIED_TOAST_ID);
              navigate({ to: "/profile", search: { socialLink: true } });
            }}
          >
            Verify My Account
          </DefaultButton>
        </div>
      ),
    });
  }, [stats?.isVerified, walletAddress, navigate]);

  const setSearchDate = useCallback(
    (date: Date) => {
      const epoch = getUtcEpochFromDate(date);

      if (search.date === epoch) {
        return;
      }

      navigate({
        from: Route.fullPath,
        replace: true,
        search: (prev) => ({
          ...prev,
          date: epoch,
        }),
      });
    },
    [navigate, search.date],
  );

  // Calculate epoch for API call: use URL date if available, otherwise use today
  // We use today as a fallback to make an initial query to get startTimestamp from backend
  const epoch = useMemo(() => {
    if (search.date && Number.isFinite(search.date)) {
      return search.date;
    }
    return getUtcEpochFromDate(new Date());
  }, [search.date]);

  const leaderboardQuery = useRushLeaderboard({
    epoch,
    loaded: true,
  });

  // Only show data if the URL date matches the query epoch (prevents showing wrong data during sync)
  // This ensures we don't show data from the initial "today" query before syncing with backend
  const shouldShowData = useMemo(() => {
    // If we have a date in URL, only show if it matches the query epoch
    if (search.date && Number.isFinite(search.date)) {
      return search.date === epoch;
    }
    // If no date in URL yet, we're still syncing - don't show data yet
    return false;
  }, [search.date, epoch]);

  // Sync URL with backend's startTimestamp only on initial load (when no date is set)
  // Once user selects a date, respect their choice
  useEffect(() => {
    // Mark as initialized if we already have a date in URL
    if (search.date && Number.isFinite(search.date)) {
      hasInitializedRef.current = true;
      return;
    }

    // Only sync on initial load when there's no date in URL
    // Wait for loading to complete before falling back to today
    if (!hasInitializedRef.current && !leaderboardQuery.isLoading) {
      if (leaderboardQuery.data?.startTimestamp) {
        const backendEpoch = leaderboardQuery.data.startTimestamp;
        const dateFromBackend = getDateFromEpoch(backendEpoch);
        if (dateFromBackend) {
          setSearchDate(dateFromBackend);
          hasInitializedRef.current = true;
        }
      } else {
        // Fallback: if no URL date and no backend data after loading completes, set today
        setSearchDate(new Date());
        hasInitializedRef.current = true;
      }
    }
  }, [
    leaderboardQuery.data?.startTimestamp,
    leaderboardQuery.isLoading,
    search.date,
    setSearchDate,
  ]);

  const rows: CustomLeaderboard[] = useMemo(() => {
    // Only use data if we should show it (prevents showing wrong data during sync)
    if (!shouldShowData) {
      return [];
    }
    const ranking = leaderboardQuery.data?.leaderboard;
    if (!Array.isArray(ranking)) {
      return [];
    }
    return ranking.map((item, index) => ({
      id: index + 1,
      position: index + 1,
      isCurrentUser: item.userPublicKey === gameWalletAddress,
      ...item,
    }));
  }, [leaderboardQuery.data?.leaderboard, gameWalletAddress, shouldShowData]);

  // Show loading if query is loading OR if we're still syncing the date
  if (leaderboardQuery.isLoading || !shouldShowData) {
    return <AnimatedLoading />;
  }

  if (leaderboardQuery.error) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center gap-3 bg-[#1D2A52]">
        <p className="text-white font-semibold text-center">
          {t("rushLeaderboard.couldNotLoadRushLeaderboard")}
        </p>
        <div className="flex flex-col items-center justify-center gap-2">
          <button
            onClick={() => leaderboardQuery.refetch()}
            className="mt-4 px-4 py-2 rounded-md bg-white/10 text-white text-sm hover:bg-white/20 transition"
          >
            {t("rushLeaderboard.tryAgain")}
          </button>
          <button className="text-white/70 text-xs">
            {t("rushLeaderboard.or")}{" "}
            <span
              className="text-blue-400 hover:text-blue-500 hover:cursor-pointer hover:underline active:scale-95 transition"
              onClick={() => navigate({ to: "/rush" })}
            >
              {t("rushLeaderboard.joinRush")}
            </span>{" "}
            {t("rushLeaderboard.whileWeFixIssue")}
          </button>
        </div>
      </div>
    );
  }

  if (!rows.length) {
    return (
      <div
        className="flex-1 min-h-0 flex flex-col items-center justify-center gap-4 relative"
        style={{
          backgroundImage:
            "url('https://cdn.navalabs.io/assets/bg-live-tournament_JeXUeg.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        <div className="w-full flex justify-between items-center absolute left-0 top-4 z-50">
          <FloatingLeftButton
            onClick={() => navigate({ to: "/rush" })}
            icon={
              <svg
                className="w-4 h-4"
                viewBox="0 0 9 8"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M3 1V0H2V1H1V2H0V3H1V4H2V5H3V4H6V7H3V8H7V7H8V6H9V3H8V2H7V1H3Z"
                  fill="white"
                />
              </svg>
            }
          />
          <DatePickerCalendar
            className="pe-2"
            value={getDateFromEpoch(search.date) ?? new Date()}
            onChange={setSearchDate}
          />
        </div>
        <div className="flex flex-col items-center justify-center gap-3">
          <img
            src="https://cdn.navalabs.io/assets/trophy-rush_WsDPjc.png"
            alt={t("rushLeaderboard.altTexts.rushTrophy")}
            className="h-16 w-16 object-contain"
          />
          <div className="flex flex-col items-center justify-center">
            <p className="text-xl text-white font-semibold">
              {t("rushLeaderboard.theresNoDataToDisplay")}
            </p>
            <p className="text-white/70 text-xs text-center mx-4">
              {t("rushLeaderboard.jumpBackIntoRushMode")}
            </p>
          </div>
          <DefaultButton
            onClick={() => navigate({ to: "/rush" })}
            className="mt-4 px-4 py-2"
          >
            {t("rushLeaderboard.joinRush")}
          </DefaultButton>
        </div>
      </div>
    );
  }

  const renderContent = () => {
    // If 3 or fewer entries, show simple list without podium
    if (rows.length <= 3) {
      return (
        <div className="flex-1 min-h-0 overflow-y-auto bg-[#1D2A52] px-2 py-2 space-y-2">
          {rows.map((item, index) => {
            const rank = index + 1;

            const prizes: Array<{
              icon: string;
              value: string | number;
              alt?: string;
              isPositive?: boolean;
            }> = [
              {
                icon: REWARD_ICONS[1],
                value: item.totalMMR,
                alt: t("rushLeaderboard.altTexts.mmr"),
                isPositive: true,
              },
            ];

            // Only add prize reward for top 3 positions
            const reward = rushLeaderboardPrizeList[rank - 1];
            if (reward) {
              prizes.unshift({
                icon: USDC_ICON,
                value: reward,
                alt: t("rushLeaderboard.altTexts.reward"),
                isPositive: true,
              });
            }

            return (
              <UserLeaderboardList
                key={item.id}
                player={item}
                rank={rank}
                isWinner={true}
                prizes={prizes}
                isHighlighted={false}
                className="text-xs!"
                shouldWarnUnverified
              />
            );
          })}
        </div>
      );
    }

    // Otherwise show full RushLeaderboard with podium (limited to 3 prizes)
    return (
      <RushLeaderboard
        rows={rows}
        isLoading={leaderboardQuery.isLoading}
        error={leaderboardQuery.error as Error | null}
        refetch={leaderboardQuery.refetch}
        stats={stats}
        walletAddress={walletAddress}
      />
    );
  };

  return (
    <div
      className="flex-1 min-h-0 flex flex-col gap-4 relative"
      style={{
        backgroundImage:
          "url('https://cdn.navalabs.io/assets/bg-live-tournament_JeXUeg.png')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      <div className="w-full flex justify-between items-center absolute left-0 top-4 z-50">
        <FloatingLeftButton
          onClick={() => navigate({ to: "/rush" })}
          icon={
            <svg
              className="w-4 h-4"
              viewBox="0 0 9 8"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M3 1V0H2V1H1V2H0V3H1V4H2V5H3V4H6V7H3V8H7V7H8V6H9V3H8V2H7V1H3Z"
                fill="white"
              />
            </svg>
          }
        />
        <DatePickerCalendar
          className="pe-2"
          value={getDateFromEpoch(search.date) ?? new Date()}
          onChange={setSearchDate}
        />
      </div>

      <div className="mt-16 px-2 flex flex-col w-full items-center justify-center gap-1">
        <h2 className="text-3xl font-bold text-white uppercase text-center text-wrap">
          {t("rushLeaderboard.rushDailyLeaderboard")}
        </h2>
        {(() => {
          const todayEpoch = getUtcEpochFromDate(new Date());
          const isToday = search.date === todayEpoch;

          // If not today, show the date and time
          if (!isToday && search.date) {
            const selectedDate = getDateFromEpoch(search.date);
            if (selectedDate) {
              const formattedDate = dayjs(selectedDate).format("MMM DD, YYYY");
              const formattedTime = dayjs(selectedDate).format("h:mm A");
              return (
                <div className="flex items-center justify-center gap-2">
                  <p className="font-extrabold text-white/80 text-base tracking-wider">
                    {formattedDate} • {formattedTime}
                  </p>
                </div>
              );
            }
          }

          // If today, show countdown
          const countdown = leaderboardQuery.data?.endTimestamp;
          if (typeof countdown !== "number")
            return (
              <AnimatedWaiting
                prefix={t("rushLeaderboard.loading")}
                className="text-xs mt-0.5"
              />
            );
          const secondsRemaining =
            countdown > 31536000
              ? Math.max(0, countdown - Math.floor(Date.now() / 1000))
              : countdown;
          return (
            <div className="flex items-center justify-center gap-2">
              <img
                src="https://cdn.navalabs.io/icons/timer_rqnxy6.png"
                className="w-3.5"
                alt={t("rushLeaderboard.altTexts.timer")}
              />
              <p className="font-extrabold text-white/80 text-base tracking-wider pl-1">
                {formatCountdown(secondsRemaining)}
              </p>
            </div>
          );
        })()}
      </div>

      <div className="flex-1 min-h-0 flex flex-col">{renderContent()}</div>
    </div>
  );
}

function formatCountdown(seconds: number) {
  const safeSeconds = Math.max(0, Math.floor(seconds));
  const days = Math.floor(safeSeconds / 86400);
  const hours = Math.floor((safeSeconds % 86400) / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  return `${days}d ${hours}h ${minutes}m`;
}
